import { toCompact, type CompactJWS } from '../utils/compact';
import { fetchHandlerApps, type HandlerAppInfo } from '../data/handlerApps';
import { setGraphqlAuthToken } from '../apollo';
import { parseEmbeddedAppearance } from '../utils/embeddedAppearance';
import {
  type SessionData,
  type RonanInteractionContext,
  type InteractionConfig,
  type AbortVariant,
} from '../types';

/**
 * Normalize the interaction context's `interactionConfig` into a fully-resolved
 * InteractionConfig, dropping an invalid embedded appearance.
 */
export function parseInteractionConfig(
  data: RonanInteractionContext['data'],
): InteractionConfig {
  const raw = data?.interactionConfig;
  return {
    demo: raw?.demo,
    purposeStatement: raw?.purposeStatement,
    retentionPolicy: raw?.retentionPolicy,
    sharingPolicy: raw?.sharingPolicy,
    deletionPolicy: raw?.deletionPolicy,
    embeddedAppearance: parseEmbeddedAppearance(raw?.embeddedAppearance),
  };
}

/**
 * Milliseconds until the graphql token expires, or `null` when the token
 * carries no `exp`. A non-positive result means the token has already expired.
 */
export function getTokenRemainingMs(graphqlToken: CompactJWS): number | null {
  const payload = graphqlToken.payload as { exp?: number };
  if (!payload.exp) return null;
  return payload.exp * 1000 - Date.now();
}

/**
 * Map a handler-apps fetch failure to the abort variant the error page should
 * show: 503 -> backend downtime, auth error -> timeout, anything else -> tech
 * failure.
 */
type ApolloLikeError = {
  networkError?: { statusCode?: number };
  graphQLErrors?: { extensions?: { code?: string } }[];
};

export function mapHandlerAppsError(err: unknown): AbortVariant {
  const { networkError, graphQLErrors } = (err ?? {}) as ApolloLikeError;
  const isAuthError = graphQLErrors?.some(
    (e) => e.extensions?.code === 'AUTHENTICATION_ERROR',
  );
  if (networkError?.statusCode === 503) return 'temporarilyUnavailable';
  if (isAuthError) return 'expired';
  return 'serverError';
}

/**
 * Outcome of turning a started interaction context into a SessionData.
 * `aborted` is only produced when a signal is supplied and fires mid-flight.
 */
export type BuildSessionResult =
  | { status: 'ok'; session: SessionData }
  | { status: 'error'; variant: AbortVariant }
  | { status: 'aborted' };

/**
 * buildSession — the shared tail of both init flows. Given a *started*
 * interaction context it validates the interaction tokens, resolves the
 * interaction config, wires up the graphql auth token, and loads the handler
 * apps referenced by the tokens.
 *
 * It is intentionally free of flow-specific concerns (OAuth interaction setup,
 * token-expiry timers, parent-origin verification, dispatching) so both the
 * redirect and embedded flows can reuse it. When a signal is supplied, work
 * that follows an `await` is guarded so an aborted flow settles as `aborted`
 * rather than dispatching a stale result.
 */
export async function buildSession(
  ctx: RonanInteractionContext,
  options: { signal?: AbortSignal } = {},
): Promise<BuildSessionResult> {
  const { signal } = options;
  const request = ctx.request;
  const tokens = request.interactionTokens;

  if (!tokens || tokens.length === 0) {
    return { status: 'error', variant: 'serverError' };
  }

  const interactionConfig = parseInteractionConfig(ctx.data);

  let handlerApps: Record<string, HandlerAppInfo> = {};
  if (request.graphqlToken) {
    setGraphqlAuthToken(toCompact(request.graphqlToken));

    const uuids = tokens
      .map((t) => t.payload.body.handlerAppUuid)
      .filter((u): u is string => !!u);

    if (uuids.length > 0) {
      try {
        handlerApps = await fetchHandlerApps(uuids);
      } catch (err) {
        if (signal?.aborted) return { status: 'aborted' };
        return { status: 'error', variant: mapHandlerAppsError(err) };
      }
    }
  }

  if (signal?.aborted) return { status: 'aborted' };

  return {
    status: 'ok',
    session: { context: ctx, tokens, handlerApps, interactionConfig },
  };
}
